import type { FilterKind, FilterState } from '@/_api/types/interfaces/filter';

/**
 * Infer the UI shape for a filter when `kind` isn't explicitly set on the
 * config. Existing `useXxxFilters` composables predate the `kind` field, so
 * we fall back to sensible defaults based on the rest of the state shape.
 */
export const inferFilterKind = (state: FilterState | undefined | null): FilterKind => {
  if (!state) return 'custom';
  if (state.kind) return state.kind;
  if (state.type === 'search') return 'search';
  if (state.component) return 'custom';
  if (Array.isArray(state.options) && state.options.length > 0) return 'list';
  if (state.type === 'boolean') return 'boolean';
  return 'text';
};

/**
 * Returns true when the list-kind options contain divider rows of the form
 * `{ type: 'label', title }` — the convention existing composables (e.g.
 * useOrderFilters' services filter) use to mark category headers in a flat
 * options array.
 */
export const isGroupedOptions = (options: any[]): boolean => {
  return Array.isArray(options) && options.some((o) => o && o.type === 'label');
};

export interface OptionGroup {
  label: string | null;
  options: any[];
}

/**
 * Split a flat options array into groups based on `{ type: 'label', title }`
 * divider rows. Options before the first label go into an unnamed group.
 */
export const groupOptions = (options: any[]): OptionGroup[] => {
  const groups: OptionGroup[] = [];
  let current: OptionGroup = { label: null, options: [] };
  for (const option of options ?? []) {
    if (option?.type === 'label') {
      if (current.options.length) groups.push(current);
      current = { label: option.title ?? '', options: [] };
    } else {
      current.options.push(option);
    }
  }
  if (current.options.length) groups.push(current);
  return groups;
};

/** Lowercase substring match for keyboard/search filtering. */
export const matchesSearch = (haystack: string | null | undefined, needle: string): boolean => {
  if (!needle) return true;
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
};
